import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MdLocationOn } from "react-icons/md";
import HomeSectionHeading from "./HomeSectionHeading";
import { faqData } from "./homeData";

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.45, ease: "easeOut" },
  },
};

/**
 * The FAQ accordion (the /faq page's UI). Defaults to the /faq page's own
 * questions; the Home page passes its own set. `locationUrl` (the /faq page
 * only) adds a "View our location" link below the questions.
 */
const HomeSectionFAQ = ({
  id = "home-faq-heading",
  title = "Frequently Asked Questions",
  subtitle = "Quick answers about ISO certification, timelines, and how MBA Solutions can help.",
  faqs = faqData,
  locationUrl,
}) => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="bg-gray-50 pt-14 lg:py-12 px-3 sm:px-6 lg:px-12 overflow-hidden" aria-labelledby={id}>
      <HomeSectionHeading
        id={id}
        title={title}
        subtitle={subtitle}
        className="mb-8 lg:mb-10"
      />
      <div className="max-w-[90vw] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.08 } },
          }}
          className="space-y-4"
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="relative bg-white rounded-2xl border border-transparent overflow-hidden group hover:border-orange-200 hover:shadow-2xl transition-all duration-300 ease-out"
            >
              <button
                type="button"
                onClick={() => toggleFaq(index)}
                className="relative w-full flex items-center justify-between text-left py-3 px-4 md:p-6 focus:outline-none z-10"
                aria-expanded={openIndex === index}
              >
                <span className="text-sm md:text-lg font-medium text-gray-900 transition-colors duration-300 group-hover:text-[var(--bg-primary)]">
                  {faq.question}
                </span>
                <motion.span
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="ml-4 text-[var(--bg-primary)] flex-shrink-0"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 9l6 6 6-6" />
                  </svg>
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                    className="overflow-hidden"
                  >
                    <div className="relative px-5 md:px-6 pb-5 md:pb-6 text-sm md:text-base text-gray-600 leading-relaxed border-t border-gray-100 z-10">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <span
                className="absolute left-0 right-0 bottom-0 h-1 bg-[#F86624] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-center"
                aria-hidden="true"
              />
            </motion.div>
          ))}
          {locationUrl && (
            <motion.div variants={itemVariants} className="pb-8 lg:pb-0">
              <a
                href={locationUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="View our location"
                aria-label="View our location on Google Maps (opens in a new tab)"
                className="group inline-flex items-center gap-3 rounded-full bg-white py-2 pl-2 pr-5 text-sm md:text-base font-medium text-gray-900 border border-transparent hover:border-orange-200 hover:shadow-lg hover:text-[var(--bg-primary)] transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F86624]"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-50 transition-colors duration-300 group-hover:bg-[#F86624]">
                  <MdLocationOn
                    size={22}
                    className="text-[#F86624] transition-colors duration-300 group-hover:text-white"
                    aria-hidden="true"
                  />
                </span>
                View our location
              </a>
            </motion.div>
          )}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="hidden md:block"
        >
          <img
            src="https://images.unsplash.com/photo-1551836022-d5d88e9218df"
            alt="ISO certification consultation and business compliance discussion"
            className="rounded-3xl shadow-2xl"
            loading="lazy"
          />
          <Link
            to="/contact"
            className="relative bottom-14 -left-6 block w-fit rounded-2xl bg-[var(--bg-primary)] p-6 text-white shadow-xl transition-transform duration-300 hover:scale-105 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F86624]"
          >
            <span className="block text-lg font-bold">Need More Help?</span>
            <span className="block text-sm opacity-90">Talk to our experts anytime</span>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default HomeSectionFAQ;
